/**
 * FAL Service Utilities
 * ======================
 * Shared utilities for all FAL services
 */

/**
 * Validate inpaint parameters
 * @param {Object} params
 * @throws {Error} If validation fails
 */
export function validateInpaintParams(params) {
  if (!params.image) {
    throw new Error('image is required');
  }
  if (!params.mask) {
    throw new Error('mask is required');
  }
  if (!params.prompt || !params.prompt.trim()) {
    throw new Error('prompt cannot be empty');
  }
}

/**
 * Validate outpaint parameters
 * @param {Object} params
 * @throws {Error} If validation fails
 */
export function validateOutpaintParams(params) {
  if (!params.image) {
    throw new Error('image is required');
  }
  if (!params.options) {
    throw new Error('options are required');
  }
}

/**
 * Validate upscale parameters
 * @param {Object} params
 * @throws {Error} If validation fails
 */
export function validateUpscaleParams(params) {
  if (!params.image) {
    throw new Error('image is required');
  }
  if (!params.model || !['clarity', 'topaz', 'seedvr', 'ideogram'].includes(params.model)) {
    throw new Error('model must be one of: "clarity", "topaz", "seedvr", "ideogram"');
  }
  if (params.options?.upscaleFactor) {
    const factor = params.options.upscaleFactor;
    if (factor < 1 || factor > 4) {
      throw new Error('upscaleFactor must be between 1 and 4');
    }
  }
}

const VIDEO_TOPAZ_MODELS = [
  'Proteus',
  'Artemis HQ',
  'Artemis MQ',
  'Artemis LQ',
  'Nyx',
  'Nyx Fast',
  'Nyx XL',
  'Nyx HF',
  'Gaia HQ',
  'Gaia CG',
];

export function validateVideoEditParams(params) {
  if (!params.video) {
    throw new Error('video is required');
  }
  if (!params.prompt || !params.prompt.trim()) {
    throw new Error('prompt cannot be empty');
  }
}

export function validateVideoBackgroundRemovalParams(params) {
  if (!params.video) {
    throw new Error('video is required');
  }
}

export function validateVideoUpscaleParams(params) {
  if (!params.video) {
    throw new Error('video is required');
  }

  const model = params.options?.model;
  if (model && !VIDEO_TOPAZ_MODELS.includes(model)) {
    throw new Error(`model must be one of: ${VIDEO_TOPAZ_MODELS.join(', ')}`);
  }

  const upscaleFactor = params.options?.upscaleFactor;
  if (upscaleFactor != null && (upscaleFactor < 1 || upscaleFactor > 8)) {
    throw new Error('upscaleFactor must be between 1 and 8');
  }
}

export function getVideoTopazModels() {
  return VIDEO_TOPAZ_MODELS.slice();
}

const USER_FRIENDLY_ERRORS = {
  content_policy_violation:
    'Your prompt was flagged by the content safety filter. Try rephrasing — avoid words that could be misinterpreted.',
  no_media_generated:
    'The model couldn\'t generate output for this prompt. Try a different description.',
  generation_timeout:
    'Generation timed out — the server took too long. Try again or use a shorter duration.',
  image_too_small: 'The source image is too small for this model.',
  image_too_large: 'The source image is too large for this model.',
};

/**
 * Extract the fal error type from the body.detail array.
 */
function getFalErrorType(error) {
  const detail = error?.body?.detail;
  if (Array.isArray(detail) && detail.length > 0) return detail[0]?.type ?? null;
  return null;
}

function getFalErrorField(error) {
  const detail = error?.body?.detail;
  if (!Array.isArray(detail) || detail.length === 0) return '';
  return (detail[0]?.loc || [])
    .filter((entry) => entry !== 'body')
    .join('.');
}

function getContentPolicyMessage(error) {
  const field = getFalErrorField(error);
  if (field.startsWith('image_urls') || field.startsWith('image_url')) {
    return 'An image reference was rejected by the content safety filter. Replace that image and try again.';
  }
  if (field.startsWith('video_urls') || field.startsWith('video_url')) {
    return 'A video reference was rejected by the content safety filter. Replace that video and try again.';
  }
  if (field.startsWith('audio_urls') || field.startsWith('audio_url')) {
    return 'An audio reference was rejected by the content safety filter. Replace that audio and try again.';
  }
  return USER_FRIENDLY_ERRORS.content_policy_violation;
}

/**
 * Extract field-level validation details from a fal error.
 * Checks for known error types first (user-friendly), then falls back
 * to raw Pydantic field messages.
 */
function extractValidationDetails(error) {
  const errorType = getFalErrorType(error);
  if (errorType === 'content_policy_violation') {
    return getContentPolicyMessage(error);
  }
  if (errorType && USER_FRIENDLY_ERRORS[errorType]) {
    return USER_FRIENDLY_ERRORS[errorType];
  }

  const detail = error?.body?.detail;
  if (!detail) return null;

  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(d => {
        const field = (d.loc || []).filter(l => l !== 'body').join('.');
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .join('; ');
  }
  return null;
}

/**
 * Classify a fal API error for UI presentation.
 * Returns { message, isContentPolicy, errorType }.
 */
export function classifyFalError(error) {
  const errorType = getFalErrorType(error);
  const message = extractValidationDetails(error) || error?.message || 'Unknown error occurred';
  return {
    message,
    isContentPolicy: errorType === 'content_policy_violation',
    errorType,
  };
}

/**
 * Sanitize error message (remove sensitive info)
 * @param {Error|Object|string} error
 * @returns {string} Safe error message
 */
export function sanitizeError(error) {
  let message;
  
  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = extractValidationDetails(error) || error.message;
  } else if (error && typeof error === 'object') {
    message = error.message || error.error || error.detail || JSON.stringify(error);
  } else {
    message = 'Unknown error occurred';
  }

  if (!message) message = 'Unknown error occurred';

  if (
    /invalid key/i.test(message)
    && (
      /header format/i.test(message)
      || /<key_id>:<key_secret>/i.test(message)
    )
  ) {
    return 'FAL proxy credentials are misconfigured. Expected FAL_API_KEY to use the <key_id>:<key_secret> format.';
  }
  
  message = message.replace(/Key\s+[A-Za-z0-9_-]+/gi, 'Key [REDACTED]');
  message = message.replace(/https?:\/\/[^\s]+/gi, (url) => {
    return url.includes('key=') || url.includes('token=') 
      ? '[URL REDACTED]' 
      : url;
  });
  
  return message;
}

/**
 * Create standardized metadata for operation
 * @param {string} operation - Operation type (inpaint/outpaint/upscale)
 * @param {string} model - Model name
 * @param {Object} params - Input parameters
 * @param {Object} result - FAL API result
 * @returns {Object} Metadata object
 */
export function createMetadata(operation, model, params, result) {
  return {
    operation,
    model,
    params: { ...params }, // Clone to avoid mutation
    timestamp: Date.now(),
    duration: result.metrics?.inference_time || null,
    requestId: result.requestId || null
  };
}

/**
 * Format service response
 * @param {string} imageUrl - Result image URL
 * @param {Object} metadata - Operation metadata
 * @returns {Object} Standardized response
 */
export function formatResponse(imageUrl, metadata) {
  return {
    imageUrl,
    metadata
  };
}

/**
 * Notify listeners that a FAL generation completed (for billing refresh).
 */
export function notifyGenerationComplete() {
  try {
    window.dispatchEvent(new CustomEvent('fal:generation-complete'));
  } catch {
    // Worker/SSR safety — no window
  }
}

/**
 * Check if an error is a cancellation error (any variant).
 * Covers AbortError, 'Operation cancelled', and 'Generation cancelled'.
 * @param {Error} error
 * @returns {boolean}
 */
export function isCancellationError(error) {
  return error?.name === 'AbortError'
    || error?.message === 'Operation cancelled'
    || error?.message === 'Generation cancelled';
}

/**
 * Throw if the operation has been cancelled.
 * Supports both AbortSignal and callback-based cancellation for
 * backward compatibility during migration.
 * @param {Object} opts
 * @param {AbortSignal} [opts.signal] - AbortSignal from AbortController
 * @param {Function} [opts.checkCancelled] - Legacy callback returning boolean
 * @throws {Error} 'Operation cancelled' if cancelled
 */
export function throwIfCancelled({ signal, checkCancelled } = {}) {
  if (signal?.aborted) {
    throw new Error('Operation cancelled');
  }
  if (checkCancelled?.()) {
    throw new Error('Operation cancelled');
  }
}

/**
 * Reject a long-running async operation with a named timeout error so callers
 * can surface a clear failure instead of leaving the UI spinning indefinitely.
 * @param {Promise} promise
 * @param {number} timeoutMs
 * @param {string} operationLabel
 * @returns {Promise<*>}
 */
export async function withTimeout(promise, timeoutMs, operationLabel = 'Operation') {
  let timeoutId;

  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = globalThis.setTimeout(() => {
          const error = new Error(`${operationLabel} timed out after ${Math.round(timeoutMs / 1000)}s`);
          error.name = 'TimeoutError';
          reject(error);
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

